import { useTranslation as useI18nTranslation } from 'react-i18next';

/**
 * Hook personnalisé pour les traductions Titaton
 * Facilite l'utilisation et ajoute des utilitaires spécifiques
 */
export const useTranslation = (namespace = 'translation') => {
  const { t, i18n } = useI18nTranslation(namespace);

  // Fonction pour changer de langue
  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
  };

  // Langue actuelle
  const currentLanguage = i18n.language;

  // Langues disponibles
  const availableLanguages = i18n.options.supportedLngs?.filter(lng => lng !== 'cimode') || ['fr', 'en'];

  // Traductions spécifiques communes
  const common = {
    loading: t('common.loading'),
    error: t('common.error'),
    success: t('common.success'),
    cancel: t('common.cancel'),
    save: t('common.save'),
    delete: t('common.delete'),
    edit: t('common.edit'),
    add: t('common.add'),
    back: t('common.back'),
    close: t('common.close'),
    confirm: t('common.confirm'),
    yes: t('common.yes'),
    no: t('common.no')
  };

  // Utilitaires pour les rôles
  const roles = {
    parent: t('roles.parent'),
    nanny: t('roles.nanny'),
    caregiver: t('roles.caregiver'),
    parentWithIcon: t('roles.parentWithIcon'),
    nannyWithIcon: t('roles.nannyWithIcon'),
    caregiverWithIcon: t('roles.caregiverWithIcon')
  };

  // Utilitaires pour les subscriptions
  const subscription = {
    free: t('subscription.free'),
    premium: t('subscription.premium'),
    freePlan: t('subscription.freePlan'),
    premiumPlan: t('subscription.premiumPlan'),
    fullAccess: t('subscription.fullAccess'),
    family: t('subscription.family'),
    upgradeToPremium: t('subscription.upgradeToPremium')
  };

  // Fonction pour formater les âges (compatible avec date-fns locale)
  const formatAge = (birthDate) => {
    if (!birthDate) return '';
    
    const birth = new Date(birthDate);
    const now = new Date();
    const diffInMonths = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
    
    if (diffInMonths < 12) {
      const diffInWeeks = Math.floor((now - birth) / (1000 * 60 * 60 * 24 * 7));
      if (diffInWeeks < 4) {
        return diffInWeeks === 1 ? 
          `1 ${t('time.week')}` : 
          `${diffInWeeks} ${t('time.weeks')}`;
      }
      return diffInMonths === 1 ? 
        `1 ${t('time.month')}` : 
        `${diffInMonths} ${t('time.months')}`;
    } else {
      const years = Math.floor(diffInMonths / 12);
      const remainingMonths = diffInMonths % 12;
      const yearText = years === 1 ? t('time.year') : t('time.years');
      
      if (remainingMonths > 0) {
        const monthText = remainingMonths === 1 ? t('time.month') : t('time.months');
        const connectors = { fr: 'et', en: 'and', it: 'e', de: 'und', es: 'y', ar: 'و' };
        const connector = connectors[i18n.language] || 'et';
        return `${years} ${yearText} ${connector} ${remainingMonths} ${monthText}`;
      }
      return `${years} ${yearText}`;
    }
  };

  // Fonction pour obtenir le bon texte selon le rôle
  const getRoleText = (role, withIcon = false) => {
    const roleKey = role === 'membre' ? 'member' : role;
    const suffix = withIcon ? 'WithIcon' : '';
    return t(`roles.${roleKey}${suffix}`, role);
  };

  // Fonction pour obtenir les textes spécifiques au dashboard selon le rôle
  const getDashboardContent = (userRole, isPremium) => {
    const isParent = userRole === 'parent';
    const roleKey = userRole === 'membre' ? 'caregiver' : 'parent';
    
    return {
      title: isParent ? t('dashboard.sections.myChildren') : t('dashboard.sections.followedChildren'),
      subtitle: t(`dashboard.subtitles.${roleKey}`),
      emptyMessage: t(`dashboard.empty.${roleKey}`),
      canAddChild: isParent,
      showPremium: isParent && !isPremium,
      welcomeTitle: roleKey === 'parent' ? t('dashboard.welcome.title') : 
                    roleKey === 'nanny' ? t('dashboard.welcome.titleNanny') : 
                    t('dashboard.welcome.titleCaregiver'),
      welcomeMessage: t(`dashboard.welcome.${roleKey}`)
    };
  };

  return {
    t,
    i18n,
    changeLanguage,
    currentLanguage,
    availableLanguages,
    common,
    roles,
    subscription,
    formatAge,
    getRoleText,
    getDashboardContent,
    
    // Fonction de compatibilité pour les anciens composants
    translate: t
  };
};

export default useTranslation;